import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { supabase } from '../config/supabaseClient';
import { useAuth } from '../contexts/AuthContext';

export default function FeedbackModal({ show, onClose }) {
  const [category, setCategory] = useState('general_feedback');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const { session, profile } = useAuth();

  const maxCharacters = 500; // Approximately 100 words
  const characterCount = message.length;

  const categoryOptions = [
    { value: 'bug_report', label: 'Bug Report' },
    { value: 'feature_request', label: 'Feature Request' },
    { value: 'general_feedback', label: 'General Feedback' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setIsSubmitting(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        setError('You must be logged in to submit feedback.');
        return;
      }

      // Get user email from session or profile
      const userEmail = session.user.email || profile?.email;
      
      if (!userEmail) {
        setError('Unable to retrieve user email. Please try again.');
        return;
      }

      const { data, error } = await supabase
        .from('feedback')
        .insert([
          {
            user_id: session.user.id,
            user_email: userEmail,
            feedback_type: category,
            message: message.trim()
          }
        ]);

      if (error) {
        console.error('Error submitting feedback:', error);
        setError('Unable to submit feedback. Please try again.');
        return;
      }

      setMessage('');
      setCategory('general_feedback');
      setSuccess(true);
      
      // Don't auto-close, let user see success message and click button

    } catch (error) {
      console.error('Error submitting feedback:', error);
      setError('Unable to submit feedback. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReturnHome = () => {
    setSuccess(false);
    onClose();
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-lg p-8 max-w-md w-full shadow-xl">
        {!success ? (
          <>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-primary w-full text-center">Send Feedback</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors absolute right-8"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-8 text-center">
              <div className="text-4xl mb-4">💬</div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                Help Us Improve
              </h3>
              <p className="text-gray-600">
                Thank you for your feedback! As we continue to grow, please leave any concerns, bugs, or suggestions.
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-100 text-red-700 rounded-lg">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="category">
                  Feedback Type
                </label>
                <select
                  id="category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                  required
                >
                  {categoryOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="message">
                  Your Feedback
                </label>
                <textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Share your thoughts, report bugs, or suggest improvements..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors resize-none"
                  rows="4"
                  maxLength={maxCharacters}
                  required
                />
                <div className="flex justify-between items-center mt-2">
                  <span className="text-sm text-gray-500">
                    Maximum {maxCharacters} characters (~100 words)
                  </span>
                  <span className={`text-sm ${characterCount > maxCharacters * 0.9 ? 'text-orange-500' : 'text-gray-500'}`}>
                    {characterCount}/{maxCharacters}
                  </span>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-3 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primaryDark transition-colors disabled:opacity-50"
                  disabled={isSubmitting || !message.trim()}
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </span>
                  ) : (
                    'Submit Feedback'
                  )}
                </button>
              </div>
            </form>
          </>
        ) : (
          // Success Message
          <div className="text-center">
            <div className="text-6xl mb-6">🎉</div>
            <h2 className="text-2xl font-bold text-primary mb-4">Thank You!</h2>
            <p className="text-gray-600 mb-8 text-lg">
              Thanks for your feedback! We love hearing from our users and will use your input to make the app even better.
            </p>
            <button
              onClick={handleReturnHome}
              className="w-full px-6 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primaryDark transition-colors"
            >
              Take Me Back to Home Page
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

FeedbackModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
}; 